import type { LandingContent } from "./types";
import heroIwiAsset from "@/assets/internal-wall-insulation/hero-iwi.jpg.asset.json";
import iwiBenefitDamp from "@/assets/lp/benefits/removes-damp.webp";
import iwiBenefitThermal from "@/assets/lp/benefits/restores-thermal.webp";
import iwiBenefitSurveyor from "@/assets/lp/benefits/surveyor-inspecting-wall.webp";
import iwiBenefitExternal from "@/assets/lp/benefits/controlled-external.webp";
import iwiBenefitDocumented from "@/assets/lp/benefits/documented.webp";
const heroIwi = heroIwiAsset.url;

/**
 * Body content for the /internal-wall-insulation page. Drives the shared
 * LpOrderedSections renderer (trust, testimonials, pricing, FAQ).
 *
 * IWI-specific bands (inspect-first, signs, signature risks block,
 * on-the-day, completion, not-every-wall, why-our-approach, social-housing)
 * are inlined in `src/components/landing/lp-internal-wall-insulation.tsx`.
 *
 * Repositioning rules: no "independent" / "no commission" language; no
 * £ savings figures; no guarantee durations.
 */
export const internalWallInsulationLp: LandingContent = {
  slug: "internal-wall-insulation",
  path: "/lp/internal-wall-insulation",
  title: "Internal Wall Insulation In South Wales",
  metaTitle:
    "Internal Wall Insulation South Wales | PAS 2030 | Design-Stage Risk Assessment",
  metaDescription:
    "PAS 2030 certified internal wall insulation across South Wales. Design-stage risk assessment, thermal-bridge management, moisture-aware build-ups, supervised install, insurance-backed guarantee.",
  defaultService: "Internal Wall Insulation",
  heroHeadline: "Internal Wall Insulation In South Wales",
  heroSubheading:
    "Warmer solid-wall and hard-to-treat homes across Wales and the South of England, using a designed, moisture-aware insulation system. Every install starts with a survey and a risk assessment, not a sales pitch.",
  heroBullets: [
    "PAS 2030 Certified",
    "Design-Stage Risk Assessment",
    "TrustMark Registered",
    "Insurance-Backed Guarantee",
    "Free Inspection",
  ],
  heroImage: heroIwi,
  heroAlt:
    "Internal wall insulation install in a UK solid-wall home with insulated boards being detailed at a window reveal",
  heroFloatImages: [
    { src: heroIwi, slug: "iwi-hero" },
    { src: iwiBenefitDamp, slug: "iwi-damp" },
    { src: iwiBenefitThermal, slug: "iwi-thermal" },
    { src: iwiBenefitSurveyor, slug: "iwi-surveyor" },
    { src: iwiBenefitExternal, slug: "iwi-external" },
    { src: iwiBenefitDocumented, slug: "iwi-documented" },
  ],
  trustItems: [
    "PAS 2030 Certified",
    "Design-Stage Risk Assessment",
    "TrustMark Registered",
    "Insurance-Backed Guarantee",
    "Free Inspection",
  ],
  problemTitle: "Why Internal Wall Insulation Matters",
  problemPoints: [
    {
      title: "Solid Walls Lose A Lot Of Heat",
      body: "Solid masonry and other hard-to-treat constructions have no cavity to insulate, so heat moves straight through the wall fabric. Done properly, IWI changes that.",
    },
    {
      title: "Poorly Executed IWI Creates Risk",
      body: "Skipping the design stage, ignoring thermal-bridge junctions or covering up untreated moisture can lead to interstitial condensation and mould. The system has to be designed for your wall.",
    },
    {
      title: "Ventilation Is Part Of The System",
      body: "Once a wall is insulated internally, moisture behaviour in the room changes. A balanced ventilation strategy keeps condensation risk managed rather than just hidden.",
    },
  ],
  clarityTitle: "What A Good IWI System Looks Like",
  clarityBody:
    "A property-specific build-up designed for your wall construction, with thermal-bridge junctions detailed, reveals and perimeters treated, an appropriate breathable or vapour-controlled approach selected, and ventilation integrated into the design.",
  productTitle: "What We Install",
  productPoints: [
    {
      title: "Designed Insulation System",
      body: "Insulated boards or framed build-ups selected for your wall, room layout and moisture risk profile, fixed and detailed to the system manufacturer's specification.",
    },
    {
      title: "Junction & Reveal Detailing",
      body: "Reveals, sills and floor/ceiling perimeters are detailed to reduce thermal-bridge risk where the insulation stops.",
    },
    {
      title: "Breathable Where Appropriate",
      body: "Where the wall, exposure and existing fabric call for it, we specify breathable build-ups so moisture can move rather than become trapped.",
    },
    {
      title: "Ventilation Integrated",
      body: "Background and extract ventilation is reviewed as part of the design, not bolted on afterwards.",
    },
  ],
  processSteps: [
    {
      step: 1,
      title: "Survey & Design",
      description:
        "Heat-loss assessment, moisture check and thermal-bridge mapping. We confirm wall construction, identify junctions that need detailing and design a system specific to your property.",
      icon: "magnifier",
    },
    {
      step: 2,
      title: "Moisture & Ventilation Plan",
      description:
        "Any existing damp or penetrating moisture is resolved first. We review ventilation provision and specify what's needed so the insulated room stays healthy.",
      icon: "magnifier",
    },
    {
      step: 3,
      title: "Supervised Installation",
      description:
        "Installed by a competent, supervised CRS team to the designed specification: boards fixed, junctions detailed, perimeters treated, ventilation integrated.",
      icon: "drill",
    },
    {
      step: 4,
      title: "Aftercare & Guarantee",
      description:
        "Installation record, guarantee registration and aftercare guidance handed over on completion. We remain available for any follow-up questions.",
      icon: "certificate",
    },
  ],
  qualificationTitle: "Is Your Wall Suitable?",
  qualificationItems: [
    "Solid-wall or hard-to-treat construction",
    "No untreated damp or penetrating moisture",
    "Room with sufficient access for a designed build-up",
    "Ventilation provision can be integrated into the design",
    "Reinstatement requirements understood and agreed",
  ],
  includedItems: [
    "Free pre-installation inspection",
    "Design-stage risk assessment",
    "PAS 2030 certified install",
    "Junction and reveal detailing",
    "Ventilation strategy review",
    "Installation record",
    "Insurance-backed guarantee registration",
    "Aftercare guidance",
  ],
  trustSectionTitle: "Evidence-Led Design, Certified Install",
  trustSectionBody:
    "Every recommendation traces back to something we observed in your property: wall construction, moisture readings, junction risks and ventilation provision. We install under PAS 2030 with an insurance-backed guarantee on completion.",
  benefits: [
    {
      title: "Designed To Your Wall",
      description: "A property-specific build-up, not a one-size-fits-all kit.",
    },
    {
      title: "Thermal Bridges Managed",
      description: "Reveals, perimeters and junctions detailed where insulation stops.",
    },
    {
      title: "Moisture-Aware Approach",
      description: "Breathable or vapour-controlled where appropriate, never a guess.",
    },
    {
      title: "Supervised Competent Install",
      description: "Installed by a CRS team under on-site supervision to spec.",
    },
  ],
  testimonialsFull: [
    {
      name: "Kevin Stevens",
      date: "September 2024",
      serviceTag: "Boiler, Internal & Cavity Wall Insulation",
      source: "trustpilot",
      quote:
        "This company carried out the work on our house for a Welsh government grant scheme, the work was carried out between July and September for a new boiler and internal wall and cavity wall insulation. Every one of the workers in the different teams, including the plumbers, electricians, insulation team, carpenters, plasterers, and the painter were all very polite, friendly and respectful of our property. We would highly recommend them.",
    },
    {
      name: "Julakha B",
      date: "December 2025",
      serviceTag: "Insulation",
      source: "google",
      quote:
        "Hi I've had insulation done today, Ryan and Nathan were amazing. They did the work, tidied up the mess and explained the process. As I have trouble keeping heat in, hopefully now this insulation will help keep heat in. Thank you so much CRS company and the team for a wonderful service.",
    },
    {
      name: "Richard W",
      date: "September 2025",
      serviceTag: "Whole Home Retrofit",
      source: "google",
      quote:
        "I cannot praise this company too highly. From first contact to completion of the work the whole experience was perfect. People turned up when they were supposed to and were all friendly, good-humoured and respectful, and they knew what they were doing. All three teams that carried out the work were well coordinated and efficient. And they all cleared up after them! Great coordination between the office and the survey and work teams.",
    },
  ],
  faqs: [
    {
      question: "Will internal wall insulation cause damp or condensation?",
      answer:
        "Not when it's designed correctly. Risk arises when IWI is installed over untreated moisture, thermal-bridge junctions are ignored, or ventilation isn't considered. Our process resolves moisture first, details junctions, and reviews ventilation as part of the design.",
    },
    {
      question: "How much room space is lost?",
      answer:
        "It depends on the system chosen for your wall. Insulated boards typically add a relatively thin layer to the inner face, while framed build-ups are thicker. We confirm the build-up depth and any reinstatement implications at the design stage.",
    },
    {
      question: "Do you treat thermal bridges at junctions and reveals?",
      answer:
        "Yes. Reveals, sills, floor and ceiling perimeters are detailed as part of the designed system, because that's where heat loss and condensation risk concentrate when IWI is fitted poorly.",
    },
    {
      question: "Is my wall suitable for IWI?",
      answer:
        "Most solid-wall and hard-to-treat constructions can be suitable, provided existing moisture is resolved, ventilation can be integrated, and the build-up is designed for the wall. We confirm suitability at the free inspection and design stage.",
    },
    {
      question: "Breathable vs non-breathable, which do you use?",
      answer:
        "It depends on your wall construction, exposure and existing fabric. Some properties call for a breathable build-up so moisture can move; others suit a vapour-controlled approach. The choice is made at the design stage based on what we observe.",
    },
    {
      question: "Do you handle reinstatement (skirtings, sockets, decoration)?",
      answer:
        "Yes. Reinstatement scope, including skirtings, sockets, radiators where relevant and decoration, is agreed at quotation so there are no surprises after the install.",
    },
  ],
  ctaTitle: "Book Your Free Inspection",
  sectionOrder: ["trust", "testimonials", "pricing", "faq"],
};
